import {
  P2GButton,
  P2GDivider,
  P2GDrawer,
  P2GGrid,
  P2GIcon,
  P2GText,
  P2GTextBox,
  useP2GMediaQueryDown,
  SvgClose
} from '@parcel2go/core-ui';
import React from 'react';
import { Tracking, TrackingDetails } from '~/lib/types/tracking-types';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface IOrderValidationProps {
  open: boolean;
  onClose: () => void;
  onValidate: (value: string) => void;
  authenticated: boolean;
  signIn: () => void;
}

const OrderValidation = ({ open, onClose, onValidate, authenticated, signIn }: IOrderValidationProps) => {
  const [postcode, setPostcode] = React.useState<string>('');
  const [postcodeError, setPostcodeError] = React.useState<string>('');
  const isBreak = useP2GMediaQueryDown('xs');
  const { t } = useTranslation(TranslationNamespaces.TRACKING);

  const handlePostcodeChange = (value: string) => {
    if (value.length === 0) {
      setPostcodeError(t('RESULT_ORDER_VALIDATE_ERROR_POSTCODE_REQUIRED', 'Postcode is required'));
    } else {
      setPostcodeError('');
    }
    setPostcode(value);
  };

  const handleValidatePostcode = (postcode: string) => {
    if (postcode.length === 0) {
      setPostcodeError(t('RESULT_ORDER_VALIDATE_ERROR_POSTCODE_REQUIRED', 'Postcode is required'));
      return;
    }
    onValidate(postcode);
    onClose();
  };

  return (
    <P2GDrawer open={open} anchor="right" onClose={onClose}>
      {isBreak && (
        <P2GGrid container justify="flex-end">
          <P2GGrid item>
            <P2GIcon size="lg" onClick={onClose}>
              <SvgClose />
            </P2GIcon>
          </P2GGrid>
        </P2GGrid>
      )}

      <P2GGrid container spacing={2} direction="column">
        <P2GGrid item>
          <P2GText variant="h5">
            <Trans t={t} i18nKey="RESULT_ORDER_VALIDATE_TITLE" defaults="See More Details" />
          </P2GText>
        </P2GGrid>
        <P2GGrid item>
          <P2GText>
            <Trans
              t={t}
              i18nKey="RESULT_ORDER_VALIDATE_DESCRIPTION"
              defaults="Enter the collection or delivery postcode for this order to see more details."
            />
          </P2GText>
        </P2GGrid>
        <P2GGrid item>
          <P2GTextBox
            id="txtValidationPostcode"
            label={t('RESULT_ORDER_POSTCODE', {
              defaultValue: 'Postcode'
            })}
            value={postcode}
            required
            onChange={handlePostcodeChange}
            error={!!postcodeError}
            helperText={postcodeError}
          />
        </P2GGrid>
        <P2GGrid item>
          <P2GButton variant="secondary" onClick={() => handleValidatePostcode(postcode)}>
            <Trans t={t} i18nKey="RESULT_ORDER_VALIDATE_BUTTON" defaults="Verify" />
          </P2GButton>
        </P2GGrid>

        {!authenticated && (
          <>
            <P2GGrid item>
              <P2GDivider />
            </P2GGrid>
            <P2GGrid item>
              <P2GText>
                <Trans
                  t={t}
                  i18nKey="RESULT_ORDER_VALIDATE_SIGNIN_DESCRIPTION"
                  defaults="If you placed this order you can sign into your account to see more details."
                />
              </P2GText>
            </P2GGrid>
            <P2GGrid item>
              <P2GButton variant="secondary" onClick={signIn}>
                <Trans t={t} i18nKey="RESULT_ORDER_VALIDATE_SIGNIN_ACTION" defaults="Sign in" />
              </P2GButton>
            </P2GGrid>
          </>
        )}
      </P2GGrid>
    </P2GDrawer>
  );
};

interface IOrderProps {
  tracking: Tracking;
  onValidate: (value: string) => void;
  authenticated: boolean;
  signIn: () => void;
  openValidation: boolean;
  onCloseValidation: () => void;
  onOpenValidation: () => void;
  parcelNumber: string;
  parcel: TrackingDetails;
  metadata?: { [name: string]: string } | null;
}

const Order: React.FC<IOrderProps> = (props) => {
  const tracking = props.tracking;
  const order = tracking.order;
  const validated = tracking.validatedOwnership;
  const isBreak = useP2GMediaQueryDown('sm');
  const { t } = useTranslation(TranslationNamespaces.TRACKING);

  const scrollToElement = (id: string) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: 'smooth' });
  };

  const hasAnyTracking = React.useMemo(() => {
    if (tracking) {
      for (const p in tracking.parcels)
        if (tracking.parcels[p].events.length > 1) {
          return true;
        }
    }
    return false;
  }, [tracking]);

  return (
    <>
      <P2GGrid container spacing={2}>
        {order.collectionCountryIso2Code !== order.deliveryCountryIso2Code && (
          <P2GGrid item xs={12}>
            <P2GGrid container direction="row" justify="space-between" alignItems="center" wrap="nowrap">
              <P2GGrid item xs={7}>
                <P2GGrid>
                  <P2GText>{order.collectionCountry}</P2GText> {'>'} <P2GText>{order.deliveryCountry}</P2GText>
                </P2GGrid>
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
        )}

        {tracking.started && hasAnyTracking && (
          <P2GGrid item xs={12}>
            <P2GGrid container direction="row" justify="space-between" alignItems="center">
              <P2GButton variant="tertiary" onClick={() => scrollToElement('tracking-history')} fullWidth={isBreak}>
                <Trans t={t} i18nKey="HISTORY_BUTTON_VIEW_HISTORY">
                  View Tracking History
                </Trans>
              </P2GButton>
            </P2GGrid>
          </P2GGrid>
        )}

        {!validated && (
          <P2GGrid item xs={12}>
            <P2GGrid container direction="row" justify="space-between" alignItems="center">
              <P2GGrid item xs={6}>
                <OrderValidation
                  open={props.openValidation}
                  onClose={props.onCloseValidation}
                  onValidate={props.onValidate}
                  authenticated={props.authenticated}
                  signIn={props.signIn}
                />
              </P2GGrid>
            </P2GGrid>
          </P2GGrid>
        )}
      </P2GGrid>
    </>
  );
};

export default Order;
